import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BlogPost } from '../../entities/BlogPost'; // שימוש ב-Entity שלנו
import axios from 'axios';
import UserService from '../../components/services/user.service';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
// ... ייתכן שתצטרכי להתאים את הנתיבים ל-emoji אם הם שונים
import { EmojiBlot, ShortNameEmoji, ToolbarEmoji, TextAreaEmoji } from 'react-quill-emoji';
import 'react-quill/dist/quill.snow.css'; // Emoji styles
import { embedMedia } from '../../utils/postContentUtils.js';

// קטגוריות לבחירה
const CATEGORIES = [
    { value: 'computer_science', label: 'מדעי המחשב' },
    { value: 'judaism_philosophy', label: 'יהדות ופילוסופיה' },
    { value: 'art', label: 'אמנות' },
    { value: 'neuroscience', label: 'מדעי המוח' },
    { value: 'psychology', label: 'פסיכולוגיה ונפש האדם' },
    { value: 'other', label: 'אחר' },
];

// Toolbar בעברית ומותאם
const quillModules = {
    toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'align': [] }, { 'direction': 'rtl' }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['link', 'image', 'emoji'], // שימי לב: emoji
        ['clean']
    ],
    // TODO when possible to install packages uncomment this section.
    // imageResize: {
    //     parchment: Quill.import('parchment'),
    //     modules: [ 'Resize', 'DisplaySize', 'Toolbar' ]
    // },
    "emoji-toolbar": true,
    "emoji-shortname": true,
    "emoji-textarea": false
};
const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'align', 'direction', 'list', 'bullet', 'link', 'image',
    'emoji'
];

export default function CreatePostPage() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const { t, i18n } = useTranslation();
    const [allPosts, setAllPosts] = useState([]);

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category: '',
        imageUrl: '',
        isPublished: false,
        isFeatured: false,
        language: 'he',
        translationOf: '',
    });
    const [message, setMessage] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const data = UserService.getCurrentUser();
                setUser(data);
            } catch {
                setUser(null);
            } finally {
                setLoading(false);
            }
        };
        fetchUser();
    }, []);

    // 2. טעינת כל הפוסטים כדי לאפשר קישור תרגומים
    useEffect(() => {
        const fetchAllPosts = async () => {
            try {
                // שימוש בפונקציה מה-Entity שיצרנו
                const posts = await BlogPost.listAll('he'); // טוען את כל הפוסטים בעברית כברירת מחדל
                const postsEn = await BlogPost.listAll('en');
                setAllPosts([...posts, ...postsEn]);
            } catch (error) {
                console.error("Failed to fetch posts for translation linking", error);
            }
        };
        fetchAllPosts();
    }, []);

    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleContentChange = value => {
        setFormData(prev => ({ ...prev, content: value }));
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage('');
        try {
            // שימוש בפונקציה מה-Entity
            await BlogPost.create(formData);
            setFormData({
                title: '', content: '', category: '', imageUrl: '',
                isPublished: false, isFeatured: false, language: 'he', translationOf: ''
            });
            setMessage(t('admin.create.success_message'));
        } catch (err) {
            setMessage(t('admin.create.error_message'));
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // 3. תרגום הקטגוריות
    const CATEGORIES = [
        { value: 'computer_science', label: t('categories.computer_science') },
        { value: 'judaism_philosophy', label: t('categories.judaism_philosophy') },
        { value: 'art', label: t('categories.art') },
        { value: 'neuroscience', label: t('categories.neuroscience') },
        { value: 'psychology', label: t('categories.psychology') },
        { value: 'other', label: t('categories.other') },
    ];

    // 4. סינון הפוסטים שניתן לקשר אליהם
    const linkablePosts = allPosts.filter(p => p.language !== formData.language);

    return (
        <div className="max-w-4xl mx-auto p-6 md:p-8 bg-white rounded-2xl shadow-lg mt-12 border">
            <h2 className="text-3xl font-bold mb-8 text-center text-[#232946]">✍️ {t('admin.create.page_title')}</h2>
            <form onSubmit={handleSubmit} className="space-y-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.create.language_label')}</label>
                        <select name="language" value={formData.language} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#eebbc3]">
                            <option value="he">עברית</option>
                            <option value="en">English</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.create.translation_of_label')}</label>
                        <select
                            name="translationOf"
                            value={formData.translationOf}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#eebbc3]"
                        >
                            <option value="">{t('admin.create.translation_of_placeholder')}</option>
                            {linkablePosts.map(post =>
                                <option key={post.id} value={post.id}>{post.title}</option>
                            )}
                        </select>
                    </div>
                </div>

                <input
                    type="text"
                    name="title"
                    placeholder={t('admin.create.title_placeholder')}
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#eebbc3]"
                    required
                />
                <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#eebbc3]"
                    required
                >
                    <option value="">{t('forms.select_category')}</option>
                    {CATEGORIES.map(cat =>
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                    )}
                </select>
                <div style={{ direction: i18n.dir(), fontFamily: "'Rubik', Arial, sans-serif" }}>
                    <ReactQuill
                        theme="snow"
                        value={formData.content}
                        onChange={handleContentChange}
                        placeholder={t('admin.create.content_placeholder')}
                        modules={quillModules}
                        formats={quillFormats}
                        style={{ minHeight: 300, height: 400 }}
                    />
                </div>
                <input
                    type="text"
                    name="imageUrl"
                    placeholder={t('admin.create.image_url_placeholder')}
                    value={formData.imageUrl}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#eebbc3]"
                />
                <div className="flex justify-between">
                    <label className="flex items-center gap-2 font-semibold">
                        <input type="checkbox" name="isFeatured" checked={formData.isFeatured} onChange={handleChange} className="accent-[#eebbc3] scale-125" />
                        {t('admin.create.featured_label')}
                    </label>
                    <label className="flex items-center gap-2 font-semibold">
                        <input type="checkbox" name="isPublished" checked={formData.isPublished} onChange={handleChange} className="accent-[#eebbc3] scale-125" />
                        {t('admin.create.publish_label')}
                    </label>
                </div>
                <div className="flex gap-2 pt-4">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 py-3 bg-[#eebbc3] hover:bg-[#ffb3c6] text-[#232946] font-bold rounded-2xl transition-all shadow disabled:opacity-50"
                    >
                        {isSubmitting ? t('status.loading') : `🚀 ${t('admin.create.submit_button')}`}
                    </button>
                    <button type="button" onClick={() => setShowPreview(p => !p)} className="flex-1 py-3 bg-[#232946] hover:bg-[#393e54] text-white font-bold rounded-2xl transition-all shadow">
                        {showPreview ? t('admin.create.preview_button_hide') : t('admin.create.preview_button_show')}
                    </button>
                </div>
            </form>

            {message && <p className="mt-6 text-center text-lg font-semibold text-green-700">{message}</p>}

            {showPreview && (
                <div className="mt-12 p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
                    <h3 className="text-xl font-semibold mb-4 text-gray-500">{t('admin.create.preview_button_show')}</h3>
                    <h2 className="text-3xl font-bold mb-4 text-[#232946]">{formData.title || t('admin.create.preview_title_placeholder')}</h2>
                    <div className="text-gray-600 mb-4">
                        <b>{t('admin.create.preview_category_label')}</b> {CATEGORIES.find(c => c.value === formData.category)?.label || '---'}
                    </div>
                    {formData.imageUrl && <img src={formData.imageUrl} alt="Post preview" className="mb-6 rounded-xl max-h-80 w-full object-cover" />}
                    <div
                        className="prose prose-lg max-w-none"
                        style={{ direction: i18n.dir() }}
                        dangerouslySetInnerHTML={{ __html: embedMedia(formData.content || `<p>${t('admin.create.preview_content_placeholder')}</p>`) }}
                    />
                </div>
            )}
        </div>
    );
}